import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import he from 'he';

// Redux Store
import { UserSessionSelector } from 'store/UserSession';
import { clientAnalyzerSelector } from 'store/Client/ClientAnalyser';
import { fetchClientNotification } from 'store/Client/ClientNotification/features';
import {
  fetchClientOrderDisplay,
  fetchClientTextOrderDisplay,
} from 'store/Client/OrderDisplay/features';


import { Form } from '@unform/web';
import AnalyseText from 'components/Admin/AnalyseText';
import { DownloadOneTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { CustomEditor } from 'components/shared/CustomEditor';
import { Loader } from 'components/shared/Loader';
import SavingIndicator from 'components/shared/AutoSaveDisplay';
import ComparisonModal from 'components/shared/modal/ComparisonModal';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import RevisionRequestModal from 'components/shared/modal/RevisionRequestModal';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import { RevisionDetails } from 'components/shared/RevisionDetails';
import { TextDisplaySidebar } from 'components/shared/TextDisplaySidebar/TextDisplaySidebar';
import { InitialTextSection } from 'components/shared/InitialTextSection';
import { FormHeader } from 'components/shared/TextDisplay/FormHeader';
import { TextDetailsSection } from 'components/shared/TextDisplay/TextDetailsSection';
import { LanguageDropdown } from 'components/shared/TextDisplay/LanguageDropdown';


import { autoSaveText } from 'helpers/CreateAutoSave';


import { ANALYSELANGAUGESLIST } from 'utils/Constants';


import {
  WordsProvider,
} from 'utils/contexts/ScoreBoldWordsContext';

import {
  SemanticScoreProvider,
} from 'utils/contexts/SemanticScoreContext';


import { useAutosave } from 'utils/hooks/useAutoSave';
import { useSidebar } from 'utils/hooks/features/textDisplay/useSidebar';
import { useClientTextForm } from 'utils/hooks/features/textDisplay/client/useClientTextForm';
import { useEditorManagement } from 'utils/hooks/features/textDisplay/useEditorManagement';
import { useTextStatus } from 'utils/hooks/features/textDisplay/useTextStatus';
import { useClientTextValidation } from 'utils/hooks/features/textDisplay/client/useClientTextValidation';
import { useClientTextSave } from 'utils/hooks/features/textDisplay/client/useClientTextSave';

function ClientTextDisplayContent() {
  const [language, setLanguage] = useState(ANALYSELANGAUGESLIST[0]);

  const [loading, setLoading] = useState(false);
  const [isSave, setIsSave] = useState(false);

  const [showComparison, setShowComparison] = useState(false);

  const [requestText, setRequestText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const [isDisabled, setIsDisabled] = useState(false);
  const [isSendRevisionDisabled, setIsSendRevisionDisabled] = useState(false);

  const [confirmation, setConfirmation] = useState(false);
  const [confirmationMsg, setConfirmationMsg] = useState('');

  const [showRevisionModal, setShowRevisionModal] = useState(false);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);
  const [isSuccess, setIsSuccess] = useState(false);

  const [initialTextWordCount, setInitialTextWordCount] = useState(0);

  const { t } = useTranslation();

  const history = useHistory();

  const dispatch = useDispatch();

  const { user } = useSelector(UserSessionSelector);
  const {
    textForm,
    orderId,
    orderType,
    isLoading,
    isError,
    writerCommandId,
    orderContentType,
    textType,
  } = useSelector(clientAnalyzerSelector);

  const { scheduleSave, isSaving } = useAutosave(autoSaveText, 1000);

  const {
    sidebarWidth,
    setSidebarWidth,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarRef,
    handleOpenSidebarAndScrollTop,
    showSidebar,
  } = useSidebar(textType,orderContentType);

  const {
   
    textTitle,
    keyword,
    url,
    titleTag,
    metaDescriptionTag,
    actualTextContent,
    actualHtmlContent,
    analyseText,
    analyseResult,
    keywords,
    combinedKeywords,
  
    setUrl,
    setTitleTag,
    setMetaDescriptionTag,
    setActualTextContent,
    setActualHtmlContent,
    setAnalyseText,
    setAnalyseResult,
    setList,
  } = useClientTextForm(textForm, isLoading);

  const { editorInitialized, handleEditorInit } = useEditorManagement();

  const { hasInitialTextContent, isRevisionSent, isEmpty } = useTextStatus(
    textForm,
    orderType,
    user.role
  );

  const { handleSave } = useClientTextSave({
    textId: textForm?.id,
    actualHtmlContent,
    actualTextContent,
    analyseResult,
    keyword,
    titleTag,
    metaDescriptionTag,
    combinedKeywords,
    url,
    t,
    setIsSave,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    setErrorSuccess,
  });

  const {
    isAllValidated,
    isPartValidated,
    userName,
    nbParts,
    nbValidatedParts,
    validateText,
  } = useClientTextValidation({
    t,
    orderId,
    writerCommandId,
    textId: textForm?.id,
    actualHtmlContent,
    actualTextContent,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setErrorSuccess,
    setIsSave,
    setConfirmation,
    setIsSuccess,
  });

  const checkValidation = useCallback(() => {
    if (
      textForm &&
      (textForm.validated ||
        ((textForm.revisionSent || textForm.globalRevisionSent) &&
          !textForm.revisionDone))
    ) {
      setIsDisabled(true);
      return;
    }
    setIsDisabled(false);
    return;
  }, [textForm]);

  const EnableSendRevision = useCallback(() => {
    if (
      textForm &&
      (textForm.validated || textForm.revisionSent || textForm.revisionDisabled)
    ) {
      setIsSendRevisionDisabled(true);
      return;
    }
    setIsSendRevisionDisabled(false);
    return;
  }, [textForm]);

  useEffect(() => checkValidation(), [checkValidation, textForm]);

  useEffect(() => EnableSendRevision(), [EnableSendRevision, textForm]);

  useEffect(() => {
    document.title = t('Typix - Détails du texte');
    window.dispatchEvent(new Event('locationchange'));
  }, [t]);

  if (isLoading || isError) return <Loader />;

  return (
    <div className="content-center">
      <Form
        onSubmit={() => Promise.resolve()}
        className="content-center"
        style={{
          transition: 'margin-right 0.3s ease',
          marginRight: sidebarCollapsed || !showSidebar ? 0 : sidebarWidth,
        }}
      >
        <FormHeader
          textTitle={textTitle}
          onBackClick={() => {
            dispatch(fetchClientOrderDisplay(orderId));
            dispatch(fetchClientTextOrderDisplay(orderId));
            dispatch(fetchClientNotification());
            history.goBack();
          }}
          onDownloadClick={() => DownloadOneTextWordType(textForm.id, user)}
        />

        <TextDetailsSection
          t={t}
          formData={{
            textTitle,
            keyword,
            url,
            titleTag,
            metaDescriptionTag,
          }}
          handlers={{
            setUrl,
            setTitleTag,
            setMetaDescriptionTag,
          }}
          disabledFields={['textTitle', 'keyword']}
        />

        {(textForm.globalRevisionSent || textForm.revisionSent) &&
          textForm.revisionText && (
            <RevisionDetails
              t={t}
              textId={textForm.id}
              revisionRequestAt={textForm.revisionRequestAt}
              revisionText={textForm.revisionText}
              revisionFileName={textForm.revisionFileName}
            />
          )}

        {hasInitialTextContent ? (
          <div className="row mt-2">
            <InitialTextSection
              textForm={textForm}
              setInitialTextWordCount={setInitialTextWordCount}
              userRole={user.role}
              t={t}
            />
          </div>
        ) : null}

        <div className="row mt-2">
          <div className="col-12">
            <div>
              <div className="label-title label-text-h">
                {t('Contenu rédigé')}
              </div>

              <div className="box-wrapper-editor">
                {(!textForm || !editorInitialized) && <Loader />}
                <CustomEditor
                  isReadOnly={!(isRevisionSent || textForm.validated)}
                  onInit={(evt, editor) => handleEditorInit(editor)}
                  content={
                    orderType !== 'PREMIUM'
                      ? actualTextContent
                      : actualHtmlContent
                  }
                  onChange={(value, editor) => {
                    let textValue = editor.getContent({ format: 'text' });
                    if (!textForm.validated || !isRevisionSent) {
                      scheduleSave(
                        textValue,
                        value,
                        textForm,
                        textForm.initialHtmlContent,
                        textForm.initialTextContent,
                        orderType
                      );
                    }

                    if (orderType !== 'PREMIUM') {
                      setActualTextContent(value);
                      setActualHtmlContent(value);
                      setAnalyseText(textValue);
                    } else {
                      setActualHtmlContent(value);
                      setActualTextContent(textValue);
                      setAnalyseText(textValue);
                    }
                  }}
                  t={t}
                  orderType={orderType}
                />
              </div>
            </div>

            <SavingIndicator isSaving={isSaving} />

            <div className="d-flex justify-content-between flex-wrap mt-3">
              <div className="box-botom-flex">
                <div style={{ paddingLeft: 10 }}>
                  <LanguageDropdown
                    t={t}
                    language={language}
                    setLanguage={setLanguage}
                  />
                </div>
              </div>

              <div className="box-botom-flex">
                <div
                  className="d-flex "
                  style={{ paddingLeft: 10, paddingTop: 10 }}
                >
                  <div>
                    <div className="d-flex flex-wrap gap-2">
                      <span>
                        {isDisabled ? (
                          <button className="bt-submit-disabled-y">
                            {t('Valider le texte')}
                          </button>
                        ) : (
                          <button
                            className="bt-submit-y"
                            style={{
                              background: '#43B929',
                              color: 'white',
                            }}
                            onClick={() => {
                              setConfirmationMsg(
                                t(
                                  'Êtes-vous sûr(e) de vouloir valider le texte ? Assurez-vous d’abord d’avoir enregistré les modifications.'
                                )
                              );
                              setConfirmation(true);
                            }}
                          >
                            {t('Valider le texte')}
                          </button>
                        )}
                      </span>

                      {orderType === 'PREMIUM' ? (
                        <span>
                          {isSendRevisionDisabled ? (
                            <button className="bt-submit-disabled-y">
                              {t('Demander une révision')}
                            </button>
                          ) : (
                            <button
                              className="bt-submit-y"
                              onClick={() => setShowRevisionModal(true)}
                            >
                              {t('Demander une révision')}
                            </button>
                          )}
                        </span>
                      ) : (
                        ''
                      )}

                      {textType !== 'TRANSLATED' &&
                      !isEmpty(textForm.url) &&
                      !isEmpty(textForm.initialTextContent) ? (
                        <span>
                          <button
                            className="bt-submit-y"
                            onClick={() => setShowComparison(true)}
                          >
                            {t('Comparer')}
                          </button>{' '}
                        </span>
                      ) : (
                        ''
                      )}
                      <span>
                        {isDisabled ? (
                          <button
                            className="bt-submit-disabled-y"
                            type="submit"
                          >
                            {t('Sauvegarder')}
                          </button>
                        ) : (
                          <button
                            className="bt-submit-y"
                            type="submit"
                            onClick={handleSave}
                          >
                            {t('Sauvegarder')}
                          </button>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>

      {showSidebar && (
        <TextDisplaySidebar
          ref={sidebarRef}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          width={sidebarWidth}
          setWidth={setSidebarWidth}          
          actualHtmlContent={actualHtmlContent}
          actualTextContent={actualTextContent}
          userRole={user.role}
          t={t}
          combinedKeywords={combinedKeywords}
          keywords={keywords}
          isKeywordBoxReadOnly
          orderContentType={orderType}
          orderWebsite={textForm?.url || ''}
          keyword={textForm?.keyword || ''}
          language={language.id}
          maxWords={textForm?.maxWords || 0}
          wordCount={actualTextContent ? actualTextContent.split(/\s+/).length : 0}
          websites={''}
          mode="client"
          textId={textForm?.id}
          onScoreDataChange={() => {}}
        />
      )}

      <RevisionRequestModal
        textId={textForm.id}
        showRevisionModal={showRevisionModal}
        requestText={requestText}
        selectedFile={selectedFile}
        handleClose={() => {
          setShowRevisionModal(false);
          setRequestText(null);
          setSelectedFile(null);
        }}
        setRequestText={(value) => setRequestText(value)}
        setSelectedFile={(value) => setSelectedFile(value)}
      />
      <ComparisonModal
        open={showComparison}
        initialText={textForm.initialTextContent}
        text={he.decode(actualTextContent)}
        handleClose={() => setShowComparison(false)}
      />
      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => {
          isSuccess && !isSave && history.goBack();
          setErrorSuccess(false);
        }}
        success={isSuccess}
        orderId={orderId}
        user={user}
        t={t}
        allTextsValidated={isAllValidated}
        partTextValidated={isPartValidated}
        userName={userName}
        nbParts={nbParts}
        nbValidatedParts={nbValidatedParts}
        writerCommandId={writerCommandId}
        toggle={() => setErrorSuccess(!errorSuccess)}
        imageUrl={errorSuccessImg}
      />
      <ConfirmationModal
        open={confirmation}
        message={confirmationMsg}
        handleClose={() => setConfirmation(false)}
        executeAction={validateText}
      />
    </div>
  );
}

function ClientTextDisplay() {
  return (
    <WordsProvider>
      <SemanticScoreProvider>
        <ClientTextDisplayContent />
      </SemanticScoreProvider>
    </WordsProvider>
  );
}

export default ClientTextDisplay;
